/**
 * What an edited copy is called, and what format it is written in.
 *
 * Its own module, and deliberately free of imports. Both functions are pure and
 * worth testing, and `render.ts` — the only other place they could sensibly
 * live — reaches the tRPC client and the media cache, which reach the browser.
 * A test importing that would drag a `localStorage` read into Node and fail for
 * reasons having nothing to do with filenames.
 */

/**
 * A copy, always — the original is never overwritten.
 *
 * Editing destructively would mean a slider could permanently destroy the only
 * copy of a photograph several people share, and no amount of undo inside one
 * session makes that acceptable.
 *
 * Repeated edits do not stack the suffix: editing "Beach (edited).jpg" gives
 * "Beach (edited 2).jpg" rather than "Beach (edited) (edited).jpg".
 */
export function editedName(original: string, mime: string): string {
  const extension =
    mime === "image/png" ? "png" : mime === "image/webp" ? "webp" : "jpg";
  const withoutExtension = original.replace(/\.[^./\\]+$/, "") || "photo";

  const existing = /^(.*) \(edited(?: (\d+))?\)$/.exec(withoutExtension);
  if (existing) {
    const next = Number(existing[2] ?? "1") + 1;
    return `${existing[1]} (edited ${next}).${extension}`;
  }

  return `${withoutExtension} (edited).${extension}`;
}

/**
 * The output format.
 *
 * JPEG for anything that arrived as a photograph, because that is what the
 * camera produced and re-encoding to PNG would multiply the size by five for no
 * gain. PNG only stays PNG, where it is probably a screenshot or a graphic and
 * the sharp edges matter.
 */
export function exportMime(sourceMime: string): string {
  return sourceMime === "image/png" ? "image/png" : "image/jpeg";
}
